import { OPTION_IDS, PAGES_RE } from "@waypoint/contracts";
import {} from "./types.js";
const SEGMENT = /^###\s+Segment\s+(\d+)\s*·\s*(.*?)\s*$/;
const HEADING = /^\s*#{1,6}(\s|$)/;
const QUESTION = /^\s*(\d+)\.(?:\s+(.*))?$/;
const OPTION = /^\s*-\s+([A-Za-z])\.(?:\s+(.*))?$/;
const WHY = /^\s*Why:(.*)$/;
const BEAT = /^\s*Beat:(.*)$/;
const CITED = /^(.*?)\s*\(Guide\s+([^()]*?)\s*\)$/;
const CORRECT = /^\*\*(.+?)\*\*\s*✓$/;
const TICK = /[✓✔]/;
const ALL_BOLD = /^\*\*.*\*\*$/;
/**
 * Reads one title's question set in the ADR-023 §1 grammar. Never throws: every problem is a coded
 * error with its line, and the KnowledgeCheck is returned only when there are none.
 */
export function parseQuestions(markdown, options) {
    const { titleId, doc, docVersion, file } = options;
    const errors = [];
    const segments = [];
    let segment;
    let question;
    const fail = (code, line, message) => {
        errors.push(file === undefined ? { code, line, message } : { code, line, message, file });
        if (question)
            question.errors += 1;
    };
    const finish = () => {
        const q = question;
        const seg = segment;
        question = undefined;
        if (!q || !seg)
            return;
        const before = errors.length;
        const correct = q.options.filter((o) => o.correct);
        if (correct.length !== 1 && !q.markFault) {
            fail("answer-count", q.line, correct.length === 0
                ? "No option is marked correct; mark exactly one in bold with a ✓"
                : `${String(correct.length)} options are marked correct; exactly one may be`);
        }
        let why = "";
        let pages = "";
        if (!q.why) {
            fail("why-missing", q.line, "The question has no Why: line");
        }
        else {
            const cited = CITED.exec(q.why.text);
            if (!cited) {
                fail("citation-missing", q.why.line, "The Why: line must end with (Guide <pages>)");
            }
            else {
                why = cited[1] ?? "";
                pages = cited[2] ?? "";
                if (why === "")
                    fail("why-missing", q.why.line, "The Why: line has no explanation");
                if (!PAGES_RE.test(pages)) {
                    fail("citation-pages", q.why.line, `"${pages}" is not a page reference like p.12, pp.11–13 or pp.12–13, 43`);
                }
            }
        }
        if (q.errors > 0 || errors.length > before || !q.why || correct[0] === undefined)
            return;
        const ordinal = seg.questions.length + 1;
        const built = {
            id: `${titleId}.${String(seg.n)}.${String(ordinal)}`,
            prompt: q.prompt,
            options: q.options.map((o) => ({ id: o.id, text: o.text })),
            answer: correct[0].id,
            why,
            citation: { doc, pages, docVersion },
        };
        if (q.beat)
            built.beat = q.beat.id;
        seg.questions.push(built);
        seg.lines.push(q.beat
            ? { prompt: q.line, why: q.why.line, beat: q.beat.line }
            : { prompt: q.line, why: q.why.line });
    };
    const rows = markdown.split(/\r?\n/);
    rows.forEach((raw, index) => {
        const line = index + 1;
        if (raw.trim() === "")
            return;
        const seg = SEGMENT.exec(raw);
        if (seg) {
            finish();
            const n = Number(seg[1]);
            const label = seg[2] ?? "";
            if (n < 1)
                fail("segment-heading", line, "Segments are numbered from 1");
            if (label === "")
                fail("segment-heading", line, "The segment heading has no label");
            const previous = segments[segments.length - 1];
            if (previous && n <= previous.n) {
                fail("segment-order", line, `Segment ${String(n)} follows segment ${String(previous.n)}; segments run in ascending order, once each`);
            }
            segment = { n, label, line, count: 0, questions: [], lines: [] };
            segments.push(segment);
            return;
        }
        if (HEADING.test(raw)) {
            fail("segment-heading", line, "A segment heading reads ### Segment <n> · <label>");
            return;
        }
        const num = QUESTION.exec(raw);
        if (num) {
            finish();
            if (!segment) {
                fail("question-outside-segment", line, "A question comes before the first segment heading");
                return;
            }
            segment.count += 1;
            question = { line, prompt: (num[2] ?? "").trim(), options: [], errors: 0, markFault: false };
            if (Number(num[1]) !== segment.count) {
                fail("question-number", line, `Question numbered ${num[1] ?? ""} should be ${String(segment.count)} in segment ${String(segment.n)}`);
            }
            if (question.prompt === "")
                fail("prompt-missing", line, "The question has no prompt");
            return;
        }
        const opt = OPTION.exec(raw);
        const why = WHY.exec(raw);
        const beat = BEAT.exec(raw);
        if (!question) {
            fail("unexpected-line", line, "This line belongs to no question");
            return;
        }
        const q = question;
        if (opt) {
            if (q.why || q.beat) {
                fail("unexpected-line", line, "Options come before the Why: and Beat: lines");
                return;
            }
            const letter = opt[1] ?? "";
            const expected = OPTION_IDS[q.options.length];
            if (expected === undefined) {
                fail("option-count", line, "A question has at most four options, a to d");
                return;
            }
            if (letter !== expected) {
                fail("option-letter", line, `Expected option ${expected}., found ${letter}.`);
                return;
            }
            const text = (opt[2] ?? "").trim();
            const marked = CORRECT.exec(text);
            const markedText = (marked?.[1] ?? "").trim();
            if (marked && markedText !== "") {
                q.options.push({ id: expected, text: markedText, correct: true });
                return;
            }
            // A malformed option is still recorded, so the next option's letter is checked against the
            // right sequence and one authoring mistake reports one error.
            q.options.push({ id: expected, text, correct: false });
            if (TICK.test(text) || ALL_BOLD.test(text)) {
                q.markFault = true;
                fail("answer-mark", line, "The correct option is both bold and ticked: **text** ✓");
                return;
            }
            if (text === "" || marked) {
                fail("option-text-missing", line, `Option ${expected}. has no text`);
            }
            return;
        }
        if (why) {
            if (q.why || q.beat) {
                fail("unexpected-line", line, "A question has one Why: line, before any Beat: line");
                return;
            }
            q.why = { text: (why[1] ?? "").trim(), line };
            return;
        }
        if (beat) {
            if (q.beat) {
                fail("unexpected-line", line, "A question names at most one beat");
                return;
            }
            if (!q.why) {
                fail("unexpected-line", line, "The Beat: line comes after the Why: line");
                return;
            }
            const id = (beat[1] ?? "").trim();
            if (!/^\S+$/.test(id)) {
                fail("beat-invalid", line, "Beat: names one beat id, with no spaces");
                return;
            }
            q.beat = { id, line };
            return;
        }
        fail("unexpected-line", line, "Not part of the question grammar");
    });
    finish();
    if (segments.length === 0 && errors.length === 0) {
        errors.push(file === undefined
            ? { code: "no-segments", line: 1, message: "The question set has no segments" }
            : { code: "no-segments", line: 1, message: "The question set has no segments", file });
    }
    if (errors.length > 0)
        return { errors };
    const built = segments.map((s) => ({
        n: s.n,
        label: s.label,
        questions: s.questions,
    }));
    return {
        check: { v: 1, titleId, segments: built },
        errors,
        lines: { segments: segments.map((s) => s.line), questions: segments.map((s) => s.lines) },
    };
}
//# sourceMappingURL=parse.js.map